import { useEffect, useState, useCallback, useRef } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import Divider from '@mui/material/Divider'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import IconButton from '@mui/material/IconButton'
import api from 'services/api'
import {
  TextArea,
  Header,
  Text,
  ExpandableTitles,
  ExpandableTranscript,
  ExpandableText,
  CopyResultsButton,
  CopyIconButton,
  formatCompareExport,
  formatVerboseResultExport,
} from './Bits'
import ModelAndThinkingConfig from './ThinkingConfig'
import { usePresetSide, PresetSelector, SchemaDescEditor } from './PresetConfig'

const SIDE_A_COLOR = '#2196f3'
const SIDE_B_COLOR = '#ff9800'

const MomentCard = ({
  moment,
  audioRef,
  playingRank,
  onPlay,
  onStop,
  accentColor,
  textExpanded,
  onToggleText,
}) => {
  const isPlaying = playingRank === moment.rank

  const handleClick = () => {
    if (isPlaying) onStop()
    else onPlay(moment)
  }

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: '8px',
        p: 1.5,
        mb: 1.5,
        borderLeft: moment.rank === 1 ? '3px solid' : 'none',
        borderColor: accentColor,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <Box sx={{ fontWeight: 700, mr: 1, fontSize: '0.85em' }}>
          #{moment.rank}
        </Box>
        <Box sx={{ fontSize: '0.8em', color: 'text.secondary' }}>
          {moment.start_s.toFixed(1)}s &ndash; {moment.end_s.toFixed(1)}s (
          {(moment.end_s - moment.start_s).toFixed(1)}s)
        </Box>
        {audioRef?.current && (
          <IconButton size="small" onClick={handleClick} sx={{ ml: 0.5 }}>
            {isPlaying ? (
              <StopIcon sx={{ fontSize: 16 }} />
            ) : (
              <PlayArrowIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        )}
      </Box>
      <Box sx={{ mb: 0.5, whiteSpace: 'pre-wrap' }}>
        <ExpandableText
          text={moment.transcript_slice}
          maxLength={80}
          fontSize="0.82em"
          expanded={textExpanded}
          onToggle={onToggleText}
        />
      </Box>
      <Box sx={{ fontSize: '0.78em', color: 'text.secondary' }}>
        {moment.why}
      </Box>
    </Box>
  )
}

const ResultColumn = ({
  label,
  result,
  accentColor,
  audioRef,
  playingRank,
  onPlay,
  onStop,
  titlesExpanded,
  onToggleTitles,
  expandedMoments,
  onToggleMoment,
  transcriptExpanded,
  onToggleTranscript,
  sourceId,
}) => {
  if (!result) return null

  return (
    <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: accentColor,
            mr: 1,
            flexShrink: 0,
          }}
        />
        <Box sx={{ fontWeight: 700, fontSize: '0.9em' }}>{label}</Box>
      </Box>

      <Box sx={{ fontSize: '0.82em', color: 'text.secondary', mb: 1 }}>
        <Box>
          {(result.latency_ms / 1000).toFixed(1)}s &middot; {result.word_count}{' '}
          words &middot; {result.duration_s}s audio &middot; {result.model_used}
        </Box>
        <Box sx={{ mt: 0.5 }}>
          Tokens: {result.prompt_tokens?.toLocaleString()} prompt &middot;{' '}
          {result.completion_tokens?.toLocaleString()} completion &middot;{' '}
          {result.thinking_tokens?.toLocaleString()}/
          {result.thinking_budget?.toLocaleString()} thinking
        </Box>
      </Box>

      {result.auto_emojis?.length > 0 && (
        <Box
          sx={{
            fontSize: '1.5em',
            letterSpacing: '0.3em',
            mb: 1,
            pl: 1,
            borderLeft: '3px solid',
            borderColor: accentColor,
          }}
        >
          {result.auto_emojis.join(' ')}
        </Box>
      )}

      {result.best_quote && (
        <>
          <Header text="Best Quote" mt={1} />
          <Box
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: '8px',
              p: 1.5,
              mb: 1,
              fontStyle: 'italic',
              fontSize: '0.85em',
              borderLeft: '3px solid',
              borderColor: accentColor,
            }}
          >
            &ldquo;{result.best_quote}&rdquo;
          </Box>
        </>
      )}

      {result.animation_context && (
        <>
          <Header text="Animation Context" mt={1} />
          <Box
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: '8px',
              p: 1,
              mb: 1,
              fontSize: '0.8em',
              borderLeft: '3px solid',
              borderColor: accentColor,
            }}
          >
            {result.animation_context}
          </Box>
        </>
      )}

      {result.titles?.length > 0 && (
        <>
          <Header text="Titles" mt={1} />
          <Box
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: '8px',
              p: 1.5,
              mb: 1,
            }}
          >
            <ExpandableTitles
              titles={result.titles}
              fontSize="0.82em"
              expanded={titlesExpanded}
              onToggle={onToggleTitles}
            />
          </Box>
        </>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
        <Header text="Moments" mt={0} />
        <CopyIconButton
          getText={() => formatVerboseResultExport(sourceId, result, label)}
          title="Copy verbose diagnostics"
        />
      </Box>
      {result.moments?.map((m) => (
        <MomentCard
          key={m.rank}
          moment={m}
          audioRef={audioRef}
          playingRank={playingRank}
          onPlay={onPlay}
          onStop={onStop}
          accentColor={accentColor}
          textExpanded={expandedMoments?.has(m.rank)}
          onToggleText={() => onToggleMoment?.(m.rank)}
        />
      ))}

      <Box sx={{ mt: 1 }}>
        <ExpandableTranscript
          transcript={result.punctuated_text}
          label="full transcript"
          fontSize="0.78em"
          expanded={transcriptExpanded}
          onToggle={onToggleTranscript}
        />
      </Box>
    </Box>
  )
}

const AutoTrimCompare = () => {
  const [sourceId, setSourceId] = useState('')
  const [presets, setPresets] = useState([])
  const sideA = usePresetSide(presets)
  const sideB = usePresetSide(presets)
  const [modelA, setModelA] = useState('gemini-2.5-flash-lite')
  const [modelB, setModelB] = useState('gemini-2.5-flash-lite')
  const [thinkingConfigA, setThinkingConfigA] = useState({})
  const [thinkingConfigB, setThinkingConfigB] = useState({})
  const [numCandidatesA, setNumCandidatesA] = useState(3)
  const [numCandidatesB, setNumCandidatesB] = useState(3)
  const [isRunning, setIsRunning] = useState(false)
  const [resultA, setResultA] = useState(null)
  const [resultB, setResultB] = useState(null)
  const [error, setError] = useState(null)
  const [audioBlobUrl, setAudioBlobUrl] = useState(null)
  const [playingRank, setPlayingRank] = useState(null)
  const [titlesExpanded, setTitlesExpanded] = useState(false)
  const [expandedMoments, setExpandedMoments] = useState(new Set())
  const [transcriptExpanded, setTranscriptExpanded] = useState(false)
  const audioRef = useRef(null)
  const endTimeRef = useRef(null)

  const toggleTitles = useCallback(() => setTitlesExpanded((v) => !v), [])
  const toggleMoment = useCallback(
    (rank) =>
      setExpandedMoments((prev) => {
        const next = new Set(prev)
        if (next.has(rank)) next.delete(rank)
        else next.add(rank)
        return next
      }),
    []
  )
  const toggleTranscript = useCallback(
    () => setTranscriptExpanded((v) => !v),
    []
  )

  const stopAudio = useCallback(() => {
    endTimeRef.current = null
    if (audioRef.current) audioRef.current.pause()
    setPlayingRank(null)
  }, [])

  const playMoment = useCallback((moment) => {
    const audio = audioRef.current
    if (!audio) return
    audio.pause()
    endTimeRef.current = moment.end_s
    audio.currentTime = moment.start_s
    const p = audio.play()
    if (p) p.catch(() => {})
    setPlayingRank(moment.rank)
  }, [])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    const onTimeUpdate = () => {
      if (
        endTimeRef.current != null &&
        audio.currentTime >= endTimeRef.current
      ) {
        audio.pause()
        endTimeRef.current = null
        setPlayingRank(null)
      }
    }
    audio.addEventListener('timeupdate', onTimeUpdate)
    return () => audio.removeEventListener('timeupdate', onTimeUpdate)
  }, [audioBlobUrl])

  useEffect(() => {
    return () => {
      if (audioBlobUrl) URL.revokeObjectURL(audioBlobUrl)
    }
  }, [audioBlobUrl])

  useEffect(() => {
    api.autoTrim
      .getPrompt()
      .then((data) => {
        setPresets(data.presets || [])
      })
      .catch((err) => setError(err.message || 'Failed to load presets'))
  }, [])

  const handleCompare = useCallback(() => {
    if (!sourceId.trim()) return
    setIsRunning(true)
    setResultA(null)
    setResultB(null)
    setError(null)
    stopAudio()

    if (audioBlobUrl) {
      URL.revokeObjectURL(audioBlobUrl)
      setAudioBlobUrl(null)
    }

    const paramsA = sideA.getApiParams()
    const paramsB = sideB.getApiParams()

    const callA = api.autoTrim.testAutoTrim(sourceId.trim(), {
      ...paramsA,
      model: modelA,
      numCandidates: numCandidatesA,
      ...thinkingConfigA,
    })
    const callB = api.autoTrim.testAutoTrim(sourceId.trim(), {
      ...paramsB,
      model: modelB,
      numCandidates: numCandidatesB,
      ...thinkingConfigB,
    })
    const callAudio = api.reclips
      .getReclipAudio(sourceId.trim(), false)
      .then((blob) => URL.createObjectURL(blob))
      .catch(() => null)

    Promise.all([callA, callB, callAudio])
      .then(([a, b, blobUrl]) => {
        setResultA(a)
        setResultB(b)
        if (blobUrl) setAudioBlobUrl(blobUrl)
      })
      .catch((err) => setError(err.message || 'Comparison failed'))
      .finally(() => setIsRunning(false))
  }, [
    sourceId,
    sideA,
    sideB,
    modelA,
    modelB,
    thinkingConfigA,
    thinkingConfigB,
    numCandidatesA,
    numCandidatesB,
    audioBlobUrl,
    stopAudio,
  ])

  const handleClear = useCallback(() => {
    setSourceId('')
    setResultA(null)
    setResultB(null)
    setError(null)
    stopAudio()
    if (audioBlobUrl) {
      URL.revokeObjectURL(audioBlobUrl)
      setAudioBlobUrl(null)
    }
  }, [audioBlobUrl, stopAudio])

  const hasResults = resultA && resultB

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Left panel: config */}
      <Box sx={{ width: 500, height: '100%', overflow: 'auto', flexShrink: 0 }}>
        <Box sx={{ fontSize: '1.2em', fontWeight: 700, mb: 1 }}>
          Compare Prompts
        </Box>
        <Text>
          Compare two prompt versions side-by-side on a single clip. Prompt A
          defaults to the current production prompt. Edit Prompt B to test your
          changes.
        </Text>

        <Header text="Source ID" />
        <Box
          sx={{
            '& input': {
              backgroundColor: 'background.paper',
              width: '100%',
              display: 'block',
              border: 0,
              p: 1.5,
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: 'divider',
              borderRadius: '8px',
              fontSize: '0.95em',
              '&:focus': { borderColor: 'white', outline: 'none' },
            },
          }}
        >
          <input
            value={sourceId}
            onChange={(e) => setSourceId(e.target.value)}
            placeholder="e.g. 123456AA-BC78-9DE0-1F2G-HI3456789JKL"
          />
        </Box>

        {/* Side A */}
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: SIDE_A_COLOR,
              mr: 1,
            }}
          />
          <Header
            text={sideA.promptModified ? 'Prompt A (Modified)' : 'Prompt A'}
            mt={0}
          />
          {sideA.promptModified && (
            <Button
              size="small"
              onClick={sideA.resetPrompt}
              sx={{ ml: 2, fontSize: '0.7em', textTransform: 'none' }}
            >
              Reset
            </Button>
          )}
        </Box>
        <PresetSelector
          presets={presets}
          presetKey={sideA.presetKey}
          onPresetChange={sideA.selectPreset}
          minS={sideA.minS}
          maxS={sideA.maxS}
          onMinChange={sideA.setMinS}
          onMaxChange={sideA.setMaxS}
          compact
        />
        <TextArea value={sideA.prompt} onChange={sideA.setPrompt} rows={5} />
        <SchemaDescEditor
          schemaDescs={sideA.schemaDescs}
          originalSchemaDescs={sideA.originalSchemaDescs}
          onSchemaDescChange={sideA.setSchemaDesc}
          onResetSchema={sideA.resetSchema}
          compact
        />
        <ModelAndThinkingConfig
          model={modelA}
          onModelChange={setModelA}
          onChange={setThinkingConfigA}
          numCandidates={numCandidatesA}
          onNumCandidatesChange={setNumCandidatesA}
          compact
        />

        {/* Side B */}
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: SIDE_B_COLOR,
              mr: 1,
            }}
          />
          <Header
            text={sideB.promptModified ? 'Prompt B (Modified)' : 'Prompt B'}
            mt={0}
          />
          {sideB.promptModified && (
            <Button
              size="small"
              onClick={sideB.resetPrompt}
              sx={{ ml: 2, fontSize: '0.7em', textTransform: 'none' }}
            >
              Reset
            </Button>
          )}
        </Box>
        <PresetSelector
          presets={presets}
          presetKey={sideB.presetKey}
          onPresetChange={sideB.selectPreset}
          minS={sideB.minS}
          maxS={sideB.maxS}
          onMinChange={sideB.setMinS}
          onMaxChange={sideB.setMaxS}
          compact
        />
        <TextArea value={sideB.prompt} onChange={sideB.setPrompt} rows={5} />
        <SchemaDescEditor
          schemaDescs={sideB.schemaDescs}
          originalSchemaDescs={sideB.originalSchemaDescs}
          onSchemaDescChange={sideB.setSchemaDesc}
          onResetSchema={sideB.resetSchema}
          compact
        />
        <ModelAndThinkingConfig
          model={modelB}
          onModelChange={setModelB}
          onChange={setThinkingConfigB}
          numCandidates={numCandidatesB}
          onNumCandidatesChange={setNumCandidatesB}
          compact
        />

        <Box
          sx={{
            mt: 2,
            mb: 4,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClear}
            disabled={!sourceId && !hasResults}
          >
            Clear
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleCompare}
            disabled={!sourceId.trim()}
            loading={isRunning}
          >
            Compare
          </LoadingButton>
        </Box>

        {error && (
          <Box
            sx={{
              backgroundColor: 'background.paper',
              p: 2,
              borderRadius: '8px',
              color: 'error.main',
              whiteSpace: 'pre-wrap',
              fontSize: '0.9em',
            }}
          >
            {error}
          </Box>
        )}
      </Box>

      <Divider orientation="vertical" sx={{ mx: 3 }} />

      {/* Right panel: results */}
      <Box sx={{ flex: 1, height: '100%', overflow: 'auto', minWidth: 0 }}>
        {!hasResults && !isRunning && (
          <Box sx={{ color: 'text.secondary', fontSize: '0.9em', mt: 4 }}>
            Results will appear here after running a comparison.
          </Box>
        )}

        {isRunning && (
          <Box sx={{ color: 'text.secondary', fontSize: '0.9em', mt: 4 }}>
            Running comparison...
          </Box>
        )}

        {hasResults && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <CopyResultsButton
                getText={() => formatCompareExport(sourceId, resultA, resultB)}
              />
            </Box>
            {audioBlobUrl && (
              <Box sx={{ mb: 2 }}>
                <audio
                  ref={audioRef}
                  src={audioBlobUrl}
                  preload="auto"
                  style={{ width: '100%', height: 32 }}
                  controls
                />
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 3 }}>
              <ResultColumn
                label="Prompt A"
                result={resultA}
                accentColor={SIDE_A_COLOR}
                audioRef={audioRef}
                playingRank={playingRank}
                onPlay={playMoment}
                onStop={stopAudio}
                titlesExpanded={titlesExpanded}
                onToggleTitles={toggleTitles}
                expandedMoments={expandedMoments}
                onToggleMoment={toggleMoment}
                transcriptExpanded={transcriptExpanded}
                onToggleTranscript={toggleTranscript}
                sourceId={sourceId}
              />
              <Divider orientation="vertical" flexItem />
              <ResultColumn
                label="Prompt B"
                result={resultB}
                accentColor={SIDE_B_COLOR}
                audioRef={audioRef}
                playingRank={playingRank}
                onPlay={playMoment}
                onStop={stopAudio}
                titlesExpanded={titlesExpanded}
                onToggleTitles={toggleTitles}
                expandedMoments={expandedMoments}
                onToggleMoment={toggleMoment}
                transcriptExpanded={transcriptExpanded}
                onToggleTranscript={toggleTranscript}
                sourceId={sourceId}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default AutoTrimCompare
